/**
 * API Service utility to handle API endpoint differences between development and production
 * This ensures that the correct API endpoints are used in both environments
 */

// Determine base URL dynamically
// 1. If developer explicitly sets REACT_APP_API_BASE, respect it.
// 2. In production, use the site origin (works behind Nginx).
// 3. In development, return an empty string so requests like `/api/...`
//    are sent to the CRA dev-server, which then proxies them to the
//    backend defined in `package.json -> proxy`.
const getBaseUrl = () => {
  // Explicit override via env variable
  if (process.env.REACT_APP_API_BASE) {
    return process.env.REACT_APP_API_BASE.replace(/\/$/, ''); // strip trailing slash
  }

  // Production – same origin (already includes protocol & host)
  if (process.env.NODE_ENV === 'production') {
    return window.location.origin;
  }

  // Development – use CRA proxy by returning empty string
  return '';
};

// Base URL for API requests
const baseUrl = getBaseUrl();

// Helpers to manage tokens
const saveAccessToken = (token) => {
  try {
    localStorage.setItem('token', token);
  } catch {}
};

const getRefreshToken = () => {
  try {
    return localStorage.getItem('refreshToken');
  } catch {
    return null;
  }
};

// Attempt to refresh access token using refreshToken
const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  const endpoint = formatEndpoint('auth/refresh');
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken })
  });
  if (!res.ok) return null;
  const data = await res.json();
  if (data && data.token) {
    saveAccessToken(data.token);
    return data.token;
  }
  return null;
};

/**
 * Format the API endpoint with the correct prefix based on environment
 * @param {string} endpoint - The API endpoint path without leading slash
 * @returns {string} - The formatted endpoint with correct prefix
 */
const formatEndpoint = (endpoint) => {
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  const prefix = '/api';
  const formatted = baseUrl ? `${baseUrl}${prefix}/${cleanEndpoint}` : `${prefix}/${cleanEndpoint}`;

  if (process.env.NODE_ENV !== 'production') {
    // Helpful for debugging in dev
    console.debug(`API → ${formatted}`);
  }

  return formatted;
};

/**
 * Get the authentication token from localStorage
 * @returns {string|null} - Authentication token or null if not found
 */
const getAuthToken = () => {
  return localStorage.getItem('token');
};

/**
 * Add authentication headers to request options if token exists
 * @param {Object} options - Fetch options
 * @returns {Object} - Updated fetch options with auth headers
 */
const addAuthHeaders = (options = {}) => {
  const token = getAuthToken();
  if (!token) return options;
  
  return {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': `Bearer ${token}`
    }
  };
};

/**
 * Make a GET request to the API
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Fetch options
 * @returns {Promise} - Fetch promise
 */
const get = async (endpoint, options = {}) => {
  let formattedEndpoint = formatEndpoint(endpoint);
  const { params, ...restOptions } = options;

  if (params) {
    // Filter out undefined or null values from params
    const filteredParams = Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null) {
        acc[key] = value;
      }
      return acc;
    }, {});

    const queryParams = new URLSearchParams(filteredParams).toString();
    if (queryParams) {
      formattedEndpoint = `${formattedEndpoint}?${queryParams}`;
    }
  }

  const doRequest = async () => {
    const authOptions = addAuthHeaders(restOptions);
    return fetch(formattedEndpoint, {
      method: 'GET',
      ...authOptions
    });
  };

  let response = await doRequest();
  if (response.status === 401) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      response = await doRequest();
    }
  }
  return response;
};

/**
 * Make a POST request to the API
 * @param {string} endpoint - API endpoint
 * @param {Object} data - Request body data
 * @param {Object} options - Fetch options
 * @returns {Promise} - Fetch promise
 */
const post = async (endpoint, data, options = {}) => {
  const formattedEndpoint = formatEndpoint(endpoint);
  const baseHeaders = options.headers || {};

  // Special handling for reports/sales endpoint
  if (endpoint === 'reports/sales') {
    
    
    // Ensure dates are properly formatted
    const processedData = { ...data };
    
    // Make sure fromDate and toDate are strings in YYYY-MM-DD format
    if (processedData.fromDate instanceof Date) {
      processedData.fromDate = processedData.fromDate.toISOString().split('T')[0];
    }
    
    if (processedData.toDate instanceof Date) {
      processedData.toDate = processedData.toDate.toISOString().split('T')[0];
    }
    
   
    
    const doRequest = async () => {
      const authOptions = addAuthHeaders({ headers: baseHeaders });
      return fetch(formattedEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authOptions.headers
        },
        body: JSON.stringify(processedData)
      });
    };
    let response = await doRequest();
    if (response.status === 401) {
      const newToken = await refreshAccessToken();
      if (newToken) {
        response = await doRequest();
      }
    }
    return response;
  }
  
  // Debug the request data for user creation
  if (endpoint === 'users/create-user') {
    
  }
  
  // Standard POST request for other endpoints
  const doRequest = async () => {
    const authOptions = addAuthHeaders({ headers: baseHeaders });
    return fetch(formattedEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authOptions.headers
      },
      body: JSON.stringify(data)
    });
  };
  let response = await doRequest();
  if (response.status === 401) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      response = await doRequest();
    }
  }
  return response;
};

/**
 * Make a PUT request to the API
 * @param {string} endpoint - API endpoint
 * @param {Object} data - Request body data
 * @param {Object} options - Fetch options
 * @returns {Promise} - Fetch promise
 */
const put = async (endpoint, data, options = {}) => {
  const formattedEndpoint = formatEndpoint(endpoint);
  
  // Prepare the request options with proper headers and body
  const requestOptions = {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...(options.headers || {})
    },
    body: JSON.stringify(data)
  };
  
  // Add auth headers
  const authOptions = addAuthHeaders(requestOptions);
  
 
  
  const doRequest = async () => {
    // Rebuild auth headers each try to pick up refreshed token
    const refreshedAuth = addAuthHeaders(requestOptions);
    return fetch(formattedEndpoint, refreshedAuth);
  };
  let response = await doRequest();
  if (response.status === 401) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      response = await doRequest();
    }
  }
  
  // Log response for debugging
 
  const responseClone = response.clone();
  try {
    const responseData = await responseClone.json();

  } catch (e) {
    const text = await response.text();
    
  }
  
  return response;
};

/**
 * Make a PATCH request to the API
 * @param {string} endpoint - API endpoint
 * @param {Object} data - Request body data
 * @param {Object} options - Fetch options
 * @returns {Promise} - Fetch promise
 */
const patch = async (endpoint, data, options = {}) => {
  const formattedEndpoint = formatEndpoint(endpoint);
  const authOptions = addAuthHeaders(options);
  const doRequest = async () => {
    const freshAuth = addAuthHeaders(options);
    return fetch(formattedEndpoint, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...freshAuth.headers
      },
      body: JSON.stringify(data),
      ...freshAuth
    });
  };
  let response = await doRequest();
  if (response.status === 401) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      response = await doRequest();
    }
  }
  return response;
};

/**
 * Make a DELETE request to the API
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Fetch options
 * @returns {Promise} - Fetch promise
 */
const del = async (endpoint, options = {}) => {
  const formattedEndpoint = formatEndpoint(endpoint);
  const authOptions = addAuthHeaders(options);
  const doRequest = async () => fetch(formattedEndpoint, {
    method: 'DELETE',
    ...addAuthHeaders(options)
  });
  let response = await doRequest();
  if (response.status === 401) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      response = await doRequest();
    }
  }
  return response;
};

// Export the API service functions
const apiService = {
  get,
  post,
  put,
  patch,
  delete: del,
  formatEndpoint
};

export default apiService;
